<template>
	<div class="content-page content-page--beta-features">
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title">Beta Feature Manager</h2>
			</div>
			<div class="content-page__actions">
				<Button
					className="btn--has-icon"
					iconClassName="icon-history"
					labelText="History"
					:btnClick="showHistory"
					:iconBeforeLabel="true"
				/>
			</div>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-one"
			ref="resultsToolBar"
		>
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					placeholder="Search by feature name"
					v-model="filters.search"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter v-if="hasResults" :counter="resultCounter" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			data-test="pageContent"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<FeaturesSummaryList :items="featuresList" />
		</ScrollContainer>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoResults v-if="!loading && !hasResults" data-test="noResults" />
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Search from '@/components/forms/search/search';
import ResultCounter from '@/site-components/result-counter/result-counter';
import { mapActions, mapGetters } from 'vuex';
import { filterAndSortArray } from '@/utils/function';
import FeaturesSummaryList from '@/sections/beta-features/summary/features-summary-list.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container.vue';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import {
	BETA_FEATURES, //Data after call api
	MODULE_NAME, //ApiBase
	SET_BETA_FEATURES //Call api, set BETA_FEATURES
} from '../beta-features-mutation-types';
import _ from 'lodash';

// libraries
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'BetaFeatures',
	components: {
		Button,
		Search,
		ResultCounter,
		ScrollContainer,
		Spinner,
		FeaturesSummaryList,
		NoResults
	},
	props: {
		search: String,
		sortBy: String,
		sortDirection: String
	},
	data() {
		return {
			featuresLogs: {},
			loading: false,
			loadingMore: false,
			page: 1,
			filters: {},
			defaultFilters: {
				search: undefined,
				sortBy: 'name',
				sortDirection: 'asc'
			}
		};
	},
	//Get BETA_FEATURES value.
	computed: {
		...mapGetters(MODULE_NAME, [BETA_FEATURES]),
		hasResults() {
			return this.featuresLogs?.items?.length && !this.loading;
		},
		resultCounter() {
			return `${this.featuresLogs?.items?.length} of ${this.featuresLogs?.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return this[BETA_FEATURES]?.countPerPage ===
				this[BETA_FEATURES]?.totalCount
				? false
				: this.featuresLogs?.items?.length <
						this.featuresLogs?.totalCount;
		},
		featuresList() {
			return this.featuresLogs?.items || [];
		}
	},
	methods: {
		...mapActions(MODULE_NAME, [SET_BETA_FEATURES]),
		showHistory() {
			this.$router.push({
				path: `/${MODULE_NAME}/history`
			});
		},
		async getAllBetaFeatures() {
			if (!this.loadingMore) {
				this.loading = true;
			}
			await this[SET_BETA_FEATURES]({ released: false });
			this.loading = false;
			this.loadingMore = false;
		},
		async fetchData(val) {
			if (_.isEmpty(this[BETA_FEATURES])) {
				await this.getAllBetaFeatures();
			}

			this.featuresLogs = _.cloneDeep(this[BETA_FEATURES]);
			if (!val) {
				return;
			}
			let items = _.cloneDeep(this[BETA_FEATURES].items);
			let result = filterAndSortArray(
				items,
				{
					description: val.toLowerCase()
				},
				null
			);
			this.featuresLogs.items = _.cloneDeep(result);
			this.featuresLogs.countPerPage = result.length;
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
			}
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: '/features',
					query: queries
				})
				.catch(() => {});
		}
	},

	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute();
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(obj.search);
				}
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				this.filters = { ...this.defaultFilters, ...newQuery };
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		let defaultBreadcrumb = [
			{
				name: 'Beta Feature Manager',
				data: { path: from.fullPath }
			}
		];
		if (to.name === 'features-history') {
			to.meta.breadcrumb = [...defaultBreadcrumb, { name: 'History' }];
		}
		if (to.name === 'features-edit') {
			to.meta.breadcrumb = [...defaultBreadcrumb, { name: 'Settings' }];
		}
		const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
		this.$store.commit('setBreadCrumbs', obj);
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
    * Beta Features Page
    * Can use SCSS variables = TRUE
    * This should only contain classes and styles used for the page layout.
*/

.content-page {
	&__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	}
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}
</style>
